import type { StoredUsers } from '~/gql/users/users'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react'
import { UserProgress } from '@zeus/index'
import { ArrowUpDown, MoreHorizontal, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_RESEND_EMAIL_VALIDATION } from '~/gql/auth/auth'
import {
  GQL_ADD_USERS_TO_GROUP,
  GQL_GROUPS,
  GQL_REMOVE_USERS_FROM_GROUP,
} from '~/gql/groups/groups'
import { GQL_USERS_WITH_FILTERS } from '~/gql/users/users'
import { client } from '~/helpers/apollo'
import { useAuth } from '~/helpers/store/auth'
import UserDeleteModal from '~/pages/backoffice/admin/user/components/UserDeleteModal'
import UserImpersonateModal from '~/pages/backoffice/admin/user/components/UserImpersonateModal'
import UserImportModal from '~/pages/backoffice/admin/user/components/UserImportModal'
import UserModal from '~/pages/backoffice/admin/user/components/UserModal'
import UserSessionsModal from '~/pages/backoffice/admin/user/components/UserSessionsModal'
import { EXPORT_MAX_ROWS, fetchAllForExport } from '~/pages/backoffice/exportPagination'
import TablePagination from '~/pages/backoffice/TablePagination'
import { useTableQuery } from '~/pages/backoffice/useTableQuery'
import { downloadCSV } from '~/utils/exportCsv/utils'

/**
 * Valide une valeur d'avancement venue de l'URL (#201).
 *
 * Une chaîne arbitraire partirait telle quelle dans l'enum GraphQL, qui la
 * rejetterait — et la page s'ouvrirait sur une erreur plutôt que sur la liste.
 * Une valeur inconnue vaut donc « aucun filtre ».
 */
function initialFilter(value: string | null): string {
  const known: string[] = [UserProgress.NOT_STARTED, UserProgress.IN_PROGRESS, UserProgress.COMPLETED]
  return value && known.includes(value) ? value : ''
}

function UserTable() {
  const { payload } = useAuth()
  const [searchParams] = useSearchParams()
  const { page, pageSize, setPage, resetPage, handleSort, queryArgs } = useTableQuery()

  const [openModal, setOpenModal] = useState<boolean>(false)
  const [openImportModal, setOpenImportModal] = useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState<boolean>(false)
  const [openImpersonate, setOpenImpersonate] = useState<boolean>(false)
  const [openSessions, setOpenSessions] = useState<boolean>(false)

  const [searchText, setSearchText] = useState('')
  const [rowSelected, setRowSelect] = useState<StoredUsers | undefined>()
  const { t } = useTranslation()

  /**
   * Libellé de l'avancement (#137), partagé par le tableau et l'export CSV :
   * l'admin doit retrouver dans le fichier les mots qu'il a lus à l'écran.
   */
  const progressLabel = (progress?: string | null) => {
    switch (progress) {
      case UserProgress.COMPLETED: return t('user.progress.completed')
      case UserProgress.IN_PROGRESS: return t('user.progress.inProgress')
      case UserProgress.NOT_STARTED: return t('user.progress.notStarted')
      default: return '/'
    }
  }

  // Filtre « groupe » (#135) : un seul groupe à la fois côté UI, même si l'API
  // accepte une liste — un multi-select ici alourdirait la barre pour un besoin
  // (« les apprenants du client A ») qui se pose au singulier.
  const [groupFilter, setGroupFilter] = useState('')
  // Lignes cochées, pour l'affectation en masse. Réinitialisées à chaque
  // changement de page ou de filtre : agir sur une sélection qu'on ne voit plus
  // à l'écran est le meilleur moyen de rattacher les mauvais apprenants.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [bulkGroupId, setBulkGroupId] = useState('')

  // Filtre « avancement » (#137) : la liste de relance manuelle se sort en
  // choisissant « en cours » ou « jamais commencé ».
  //
  // Amorçable par l'URL (`?progress=NOT_STARTED`), parce que l'accueil (#201) y
  // envoie depuis son compteur « jamais commencé » : sans ça, le lien
  // atterrirait sur la liste complète et la personne devrait refaire à la main
  // le tri qu'on venait de lui annoncer. Lu une seule fois, à l'ouverture — la
  // suite appartient au sélecteur, qui ne réécrit pas l'URL.
  const [progressFilter, setProgressFilter] = useState(
    () => initialFilter(searchParams.get('progress')),
  )

  const { data: groupsData, refetch: refetchGroups } = useQuery(GQL_GROUPS, { client })
  const groups = groupsData?.groups ?? []

  const [addUsersToGroup] = useMutation(GQL_ADD_USERS_TO_GROUP, { client })
  const [removeUsersFromGroup] = useMutation(GQL_REMOVE_USERS_FROM_GROUP, { client })

  const filterBy = groupFilter || progressFilter
    ? {
        ...(groupFilter && { group: [groupFilter] }),
        ...(progressFilter && { progress: progressFilter as UserProgress }),
      }
    : undefined

  const { data, refetch: fetchUsers, loading } = useQuery(GQL_USERS_WITH_FILTERS, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      input: {
        ...queryArgs,
        searchInput: searchText,
        filterBy,
      },
    },
  })

  const [sendEmailValidation] = useMutation(GQL_RESEND_EMAIL_VALIDATION)

  // network-only : l'export part en plusieurs tranches, qui doivent toutes venir
  // de la même lecture serveur — une tranche servie depuis le cache mélangerait
  // deux états de la base dans un même fichier.
  const [fetchExportedUsers] = useLazyQuery(GQL_USERS_WITH_FILTERS, { fetchPolicy: 'network-only' })

  const handleRefreshUser = async () => {
    await fetchUsers()
  }

  async function exportCsv() {
    const exported = await fetchAllForExport(async (skip, take) => {
      const chunk = await fetchExportedUsers({
        variables: {
          input: {
            skip,
            take,
            searchInput: searchText,
            // L'export reprend le filtre de l'écran : sortir « les 40 apprenants
            // du client A » est précisément ce que l'export sert à faire (#135).
            filterBy,
          },
        },
      })
      const result = chunk.data?.getFilteredUsers
      return result?.users ? { rows: result.users, total: result.total } : null
    })
    if (!exported) return

    if (exported.truncated) {
      toast.warning(t('export.truncated', { max: EXPORT_MAX_ROWS, total: exported.total }))
    }

    downloadCSV(
      t('common.users'),
      [
        t('common.email'),
        t('common.firstname'),
        t('common.lastname'),
        t('common.role'),
        t('common.groups'),
        t('user.progress.label'),
        t('common.lastConnection'),
      ],
      exported.rows.map((user) => {
        const date = new Date(user.updatedAt as Date)
        const dateFr = date.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
        return [
          user.email,
          user.firstName,
          user.lastName,
          user.role.name,
          (user.groups ?? []).map(g => g.name).join(', '),
          progressLabel(user.progress),
          user.updatedAt ? dateFr : '/',
        ]
      }),
    )
  }

  /** Bascule une ligne dans la sélection d'affectation en masse. */
  const toggleRow = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const pageUserIds = data?.getFilteredUsers.users?.map(u => u.id) ?? []
  const allPageSelected = pageUserIds.length > 0 && pageUserIds.every(id => selectedIds.has(id))

  const togglePage = () => {
    setSelectedIds(current => (
      allPageSelected
        ? new Set([...current].filter(id => !pageUserIds.includes(id)))
        : new Set([...current, ...pageUserIds])
    ))
  }

  /** Affecte ou retire la sélection courante du groupe choisi dans la barre. */
  const applyBulk = async (action: 'add' | 'remove') => {
    if (!bulkGroupId || selectedIds.size === 0) return
    const variables = { groupId: bulkGroupId, userIds: [...selectedIds] }
    try {
      if (action === 'add') await addUsersToGroup({ variables })
      else await removeUsersFromGroup({ variables })
      toast.success(t(action === 'add' ? 'groups.bulk.added' : 'groups.bulk.removed'))
      setSelectedIds(new Set())
      // Les compteurs de membres du filtre bougent aussi : les rafraîchir, sinon
      // la liste déroulante affiche des volumes périmés.
      await Promise.all([fetchUsers(), refetchGroups()])
    } catch {
      toast.error(t('groups.bulk.failed'))
    }
  }

  /** Avancement agrégé sur toutes les inscriptions de l'apprenant (#137). */
  const getProgressBadge = (progress?: string | null) => {
    switch (progress) {
      case UserProgress.COMPLETED:
        return <Badge variant="success">{progressLabel(progress)}</Badge>
      case UserProgress.IN_PROGRESS:
        return <Badge variant="warning">{progressLabel(progress)}</Badge>
      case UserProgress.NOT_STARTED:
        return <Badge variant="secondary">{progressLabel(progress)}</Badge>
      default:
        return <span className="text-muted-foreground">—</span>
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ENABLED':
        return (
          <Badge variant="success">
            {t('status.active')}
          </Badge>
        )
      case 'PENDING_ACTIVATION':
        return (
          <Badge variant="warning">
            {t('status.pending')}
          </Badge>
        )
      default:
        return (
          <Badge variant="secondary">
            {status}
          </Badge>
        )
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Input
            placeholder={t('common.search')}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="max-w-sm"
          />
          <select
            value={groupFilter}
            onChange={(e) => {
              setGroupFilter(e.target.value)
              resetPage()
              setSelectedIds(new Set())
            }}
            className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
            aria-label={t('common.groups')}
          >
            <option value="">{t('groups.all')}</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select
            value={progressFilter}
            onChange={(e) => {
              setProgressFilter(e.target.value)
              resetPage()
              setSelectedIds(new Set())
            }}
            className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
            aria-label={t('user.progress.label')}
          >
            <option value="">{t('user.progress.all')}</option>
            <option value={UserProgress.NOT_STARTED}>{t('user.progress.notStarted')}</option>
            <option value={UserProgress.IN_PROGRESS}>{t('user.progress.inProgress')}</option>
            <option value={UserProgress.COMPLETED}>{t('user.progress.completed')}</option>
          </select>
          <Button variant="outline" onClick={handleRefreshUser}>
            <RefreshCw className="size-4" />
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={() => setOpenModal(true)}>
            {t('common.add')}
          </Button>
          <Button variant="outline" onClick={() => setOpenImportModal(true)}>
            {t('common.import')}
          </Button>
          <Button variant="outline" onClick={exportCsv}>
            {t('common.export')}
          </Button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/50 p-3">
          <span className="text-sm font-medium">
            {t('groups.bulk.selected', { count: selectedIds.size })}
          </span>
          <select
            value={bulkGroupId}
            onChange={e => setBulkGroupId(e.target.value)}
            className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
            aria-label={t('common.group')}
          >
            <option value="">{t('groups.select')}</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <Button size="sm" disabled={!bulkGroupId} onClick={() => applyBulk('add')}>
            {t('groups.bulk.add')}
          </Button>
          <Button size="sm" variant="outline" disabled={!bulkGroupId} onClick={() => applyBulk('remove')}>
            {t('groups.bulk.remove')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
            {t('sessions.participants.clear')}
          </Button>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={togglePage}
                  aria-label={t('groups.bulk.selectPage')}
                />
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('email')}
                  className="h-8 w-full justify-start text-left font-medium"
                >
                  {t('common.email')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('firstName')}
                  className="h-8 w-full justify-start text-left font-medium"
                >
                  {t('common.firstname')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('lastName')}
                  className="h-8 w-full justify-start text-left font-medium"
                >
                  {t('common.lastname')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('role')}
                  className="h-8 w-full justify-start text-left font-medium"
                >
                  {t('common.role')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>{t('common.groups')}</TableHead>
              <TableHead>{t('user.progress.label')}</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('lastConnectionAt')}
                  className="h-8 w-full justify-start text-left font-medium"
                >
                  {t('common.lastConnection')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort('status')}
                  className="h-8 w-full justify-start text-left font-medium"
                >
                  {t('common.status')}
                  <ArrowUpDown className="ml-2 size-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[100px]">
                {t('common.action')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? (
                  <TableRow>
                    <TableCell colSpan={10} className="h-24 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                )
              : data?.getFilteredUsers.users.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={10} className="h-24 text-center">
                        No results.
                      </TableCell>
                    </TableRow>
                  )
                : (
                    data?.getFilteredUsers.users.map(user => (
                      <TableRow className={payload?.email === user.email ? 'bg-accent text-accent-foreground hover:bg-accent' : ''} key={user.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(user.id)}
                            onChange={() => toggleRow(user.id)}
                            aria-label={user.email}
                          />
                        </TableCell>
                        <TableCell>
                          {user.email}
                        </TableCell>
                        <TableCell>{user.firstName}</TableCell>
                        <TableCell>{user.lastName}</TableCell>
                        <TableCell>{user.role.name}</TableCell>
                        <TableCell>
                          {user.groups?.length
                            ? (
                                <div className="flex flex-wrap gap-1">
                                  {user.groups.map(g => (
                                    <Badge key={g.id} variant="secondary">{g.name}</Badge>
                                  ))}
                                </div>
                              )
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {getProgressBadge(user.progress)}
                        </TableCell>
                        <TableCell>
                          {user.updatedAt
                            ? new Date(user.updatedAt as unknown as string).toLocaleString('fr')
                            : '/'}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(user.status)}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setRowSelect(user)
                                  setOpenSessions(true)
                                }}
                              >
                                {t('user.sessions.view')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setRowSelect(user)
                                  setOpenModal(true)
                                }}
                              >
                                {t('common.update')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setRowSelect(user)
                                  setOpenDelete(true)
                                }}
                              >
                                {t('common.delete')}
                              </DropdownMenuItem>
                              {
                                payload?.email !== user.email && user.role?.name !== 'Admin' && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setRowSelect(user)
                                      setOpenImpersonate(true)
                                    }}
                                  >
                                    {t('common.impersonate')}
                                  </DropdownMenuItem>
                                )
                              }

                              {
                                user.status === 'PENDING_ACTIVATION' && (
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await sendEmailValidation({
                                          variables: {
                                            email: user.email,
                                          },
                                        })
                                        toast.success(t('resend.email.success'))
                                      } catch {
                                        toast.success(t('resend.email.failed'))
                                      }
                                    }}
                                  >
                                    {t('resend.email')}
                                  </DropdownMenuItem>
                                )
                              }
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={page}
        pageSize={pageSize}
        total={data?.getFilteredUsers.total}
        onPageChange={setPage}
      />

      {
        openModal && rowSelected && (
          <UserModal
            open={openModal}
            onCancel={() => { setOpenModal(false); setRowSelect(undefined) }}
            onSuccess={fetchUsers}
            defaultValues={
              {
                id: rowSelected.id,
                email: rowSelected.email,
                firstName: rowSelected.firstName,
                lastName: rowSelected.lastName,
                roleId: rowSelected.role.id,
              }
            }
          />
        )
      }

      {
        openModal && !rowSelected && (
          <UserModal
            open={openModal}
            onCancel={() => { setOpenModal(false); setRowSelect(undefined) }}
            onSuccess={fetchUsers}
          />
        )
      }

      {openImportModal && (
        <UserImportModal
          open={openImportModal}
          onCancel={() => setOpenImportModal(false)}
          onSuccess={handleRefreshUser}
        />
      )}

      {rowSelected && (
        <>
          <UserDeleteModal
            open={openDelete}
            onCancel={() => setOpenDelete(false)}
            onSuccess={handleRefreshUser}
            user={rowSelected}
          />

          <UserImpersonateModal
            open={openImpersonate}
            onCancel={() => setOpenImpersonate(false)}
            user={rowSelected}
          />

          <UserSessionsModal
            open={openSessions}
            onCancel={() => setOpenSessions(false)}
            user={rowSelected}
          />
        </>
      )}
    </div>
  )
}

export default UserTable
